import { Link } from "react-router-dom";
import { Ruler } from "lucide-react";
import { guidesForCategory, guidesForProductType, type GuideLink } from "@/data/guidesByCategory";
import { PRODUCT_TYPE_TOOL } from "../../../scripts/category-tools.mjs";

interface BeforeYouBuyProps {
  /** Shopify productType, e.g. "Area Rugs". */
  productType?: string;
  /** Category slug derived on the PDP, when one matched. */
  categorySlug?: string;
}

function resolveGuides({ productType, categorySlug }: BeforeYouBuyProps): GuideLink[] {
  // The product type's own guides first — the same map the prerendered "Buying
  // guide" line reads — then the guides written for its category. Nothing after
  // that: the old keyword guesses and living-room last resort put coffee-table
  // guides on pantry cabinets and the bedding guide on shower doors, so a
  // product with no relevant guide now shows none.
  const out: GuideLink[] = [];
  for (const pool of [guidesForProductType(productType), guidesForCategory(categorySlug)]) {
    for (const g of pool) {
      if (out.length < 3 && !out.some((x) => x.slug === g.slug)) out.push(g);
    }
  }
  return out;
}

/**
 * "Before you buy" — 2–3 journal guides relevant to this product.
 * Editorial links only; no price or buy language.
 */
export const BeforeYouBuy = (props: BeforeYouBuyProps) => {
  const guides = resolveGuides(props);
  // Size-led product types also get their free calculator (scripts/category-tools.mjs).
  const tool = props.productType ? PRODUCT_TYPE_TOOL[props.productType] : undefined;
  if (!guides.length && !tool) return null;

  return (
    <section className="border-t border-border">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground mb-3 font-medium">
          Before you buy
        </p>
        <p className="text-[15px] text-muted-foreground leading-[1.7] mb-5 max-w-[60ch]">
          LoftLoom guides to help you choose well.
        </p>
        <ul className="space-y-2.5">
          {guides.map((g) => (
            <li key={g.slug}>
              <Link
                to={`/ideas/${g.slug}`}
                className="text-[16px] md:text-[17px] text-foreground underline decoration-border hover:decoration-foreground underline-offset-4 transition-colors"
              >
                {g.title}
              </Link>
            </li>
          ))}
        </ul>
        {tool && (
          <Link
            to={tool.path}
            className="mt-5 inline-flex items-center gap-2 text-[15px] text-foreground underline decoration-border hover:decoration-foreground underline-offset-4 transition-colors"
          >
            <Ruler className="h-4 w-4 shrink-0" aria-hidden="true" />
            {tool.prompt}
          </Link>
        )}
      </div>
    </section>
  );
};
